import React, { useState, useEffect } from 'react';
import DataTable from "../../Common/DataTable/DataTable";
import styles from "./Customer.module.css";
import { ViewDocumentList, updateAccountStatus } from "../../api";
import CommentModal from "../../Common/CommentModal/commentModal";
import CustomSpinner from '../../Common/Spinner';
import { IoDocumentsOutline } from "react-icons/io5";

const Documents = ({ custId, userData, featureAccess, getDetails }) => {
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isComment, setIsComment] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [comment, setComment] = useState("");
  const [showActions, setShowActions] = useState(false);
  const [loading, setLoading] = useState(false);

  const documentColumns = [
  {
    Header: "Document Type",
    accessor: "type",
    formatText: true,
  },
  {
    Header: "Proof Type",
    accessor: "proofType",
    formatText: true,
  },
  {
    Header: "Created At",
    accessor: "createdAt",
  },
  {
    Header: "Status",
    accessor: "status",
    Cell: (props) => (
      <span
        style={{
          color: getStatusColor(props.row.original.status),
          fontWeight: "500",
        }}
      >
        {props.row.original.status}
      </span>
    ),
  },
  {
    Header: "Front Document",
    accessor: "docFrontUrl",
    Cell: ({ row }) =>
      row.original.docFrontUrl ? (
        <button
          onClick={() => handleViewDocuments({ url: row.original.docFrontUrl })}
          className={styles.viewButton}
          title="View Front Document"
        >
          <IoDocumentsOutline size={20}/>  
        </button>
      ) : (
        <span>--</span>
      ),
  },
  {
    Header: "Back Document",
    accessor: "docBackUrl",
    Cell: ({ row }) =>
      row.original.docBackUrl ? (
        <button
          onClick={() => handleViewDocuments({ url: row.original.docBackUrl })}
          className={styles.viewButton}
          title="View Back Document"
        >
           <IoDocumentsOutline size={20} />  
        </button>
      ) : (
        <span>--</span>
      ),
  },
];

  const statusShowActions = () => {
    const isManualReview = userData?.nextStep === "MANUAL_REVIEW";
    const hasPendingDocuments = documents.some(doc => doc.status?.toUpperCase() === "PENDING");
    return isManualReview && hasPendingDocuments;
  };
  useEffect(() => {
    setShowActions(statusShowActions());
  }, [userData, documents]);

  const fetchDocuments = async () => {
    try {
      setDataLoading(true);
      const response = await ViewDocumentList(custId);

      if (response?.code === 200) {
        setDocuments(response.data);
      } else {
        setError("Documents not found");
      }
    } catch (error) {
      setError("Error fetching documents");
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [custId]);

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setIsModalOpen(true);
    setErrorMessage("");
    setSuccessMessage("");
    setComment("");
  };

  const updateStatus = async () => {
    if (loading) return;
    setLoading(true);
    const updateData = {
      comment: isComment,
      status: selectedStatus,
    };

    try {
      const resp = await updateAccountStatus(custId, updateData);

      if (resp?.code === 200) {
        setSuccessMessage("Status updated successfully!");
        setErrorMessage("");
        setTimeout(async () => {
          setIsModalOpen(false);
          if (getDetails) {
            await getDetails(custId);
          }
          await fetchDocuments();
        }, 3000);
        return true;
      } else {
        setErrorMessage(resp?.data?.message || "Failed to update status.");
        return false;
      }
    } catch (e) {
      setErrorMessage("An error occurred while updating status.");
      return false;
    }finally {
      setLoading(false);
    }
  };

  const handleViewDocuments = (doc) => {
    const url = doc.url;
    const left = window.screen.width / 2 - 400;
    const top = window.screen.height / 2 - 350;
    const newWindow = window.open(
      "",
      "",
      `width=800,height=700,left=${left},top=${top}`
    );
    const isPdf = url.includes(".pdf");
    if (newWindow) {
      const preventSaveCSS = `
        * {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
        body {
          margin: 0;
          overflow: hidden;
        }
      `;

      const preventSaveJS = `
        document.addEventListener('contextmenu', e => e.preventDefault());
        document.addEventListener('keydown', e => {
          if (
            (e.ctrlKey || e.metaKey) && e.key === 's' ||
            (e.ctrlKey || e.metaKey) && e.key === 'p' ||
            (e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'i' ||
            e.key === 'PrintScreen'
          ) {
            e.preventDefault();
            return false;
          }
        });
      `;
      newWindow.document.write(`
        <html>
          <head>
            <title>Document Preview</title>
          </head>
          <body style="margin:0">
            ${
              isPdf
              ? `<iframe 
              src="https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true" 
              style="width:100%; height:100%; border:none;" 
              sandbox="allow-same-origin allow-scripts"
              oncontextmenu="return false;"
            ></iframe>`
                : `<div style="width:100%; height:100%; display:flex; justify-content:center; align-items:center; background:#000;">
                  <img 
                    src="${url}" 
                    alt="Document" 
                    style="max-width:100%; max-height:100%; object-fit:contain;" 
                    oncontextmenu="return false;"
                    draggable="false"
                  />
                </div>`
            }
            <script>${preventSaveJS}</script>
            </body>
        </html>
      `);
      newWindow.document.close();
      newWindow.addEventListener("beforeunload", () => {
        URL.revokeObjectURL(url);
      });
    }
  };
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "#4CAF50";
      case "PENDING":
        return "#FF9800";
      case "REJECTED":
        return "#F44336";
      default:
        return "#FFFFFF";
    }
  };
  return (
    <div className={styles.container}>
      {showActions && featureAccess?.writeAccess  &&(
        <div className={styles.actionButton}>
          <button
            onClick={() => handleStatusChange("COMPLETE")}
            className={styles.approveButton}
          >
            Approve
          </button>
          <button
            onClick={() => handleStatusChange("REJECTED")}
            className={styles.rejectButton}
          >
            Reject
          </button>
        </div>
      )}

      {dataLoading ? (
        <CustomSpinner />
      ) : Array.isArray(documents) && documents.length > 0 ? (
        <DataTable
          columns={documentColumns}
          list={documents}
          rowClick={false}
          handleDocumentDetails={handleViewDocuments}
        />
      ) : (
        <p style={{ color: "white", marginTop: "20px", textAlign: "center" }}>
          No Data Found
        </p>
      )}
      <CommentModal
        setIsComment={setIsComment}
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        errorMessage={errorMessage}
        error={error}
        setError={setError}
        comment={comment}
        setComment={setComment}
        onSave={async () => {
          const success = await updateStatus();

          if (success) {
            setErrorMessage("");
            setSuccessMessage( selectedStatus==="COMPLETE" ? "Account approved successfully!":"Account rejected successfully!");
          }
        }}
        loading={loading}
      />
    </div>
  );
};
export default Documents;